import type { ReactElement } from 'react';

import { getFragmentData } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';

import { useMissionContextOptional } from '../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { MissionContextStatus } from '../../../contexts/MissionContext/types/mission-context.type';
import { FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment } from '../../../pages/Folder/fragments/folder-business-entity-consolidation.fragment';

import { useGoogleApi } from '../../Map/hooks/google/useGoogleApi.hook';
import { AddressWithMapCmp_Address_Fragment } from '../../AddressWithMap';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

export const useSIGCarteLieuMission = (
  parameters: ResolverParameters,
): ResolverResult<ReactElement> => {
  const missionContext = useMissionContextOptional();

  const consolidation = getFragmentData(
    FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment,
    missionContext?.mission?.consolidation,
  );

  const address = getFragmentData(
    AddressWithMapCmp_Address_Fragment,
    consolidation?.address,
  );

  const { staticMapPreviewUrl } = useGoogleApi({
    address,
  });

  if (!parameters.enabled) {
    return {
      data: undefined,
      error: null,
      isSuccess: false,
      isLoading: false,
    };
  }

  invariant(!!missionContext, 'Mission context is required');

  const { status } = missionContext;

  switch (status) {
    case MissionContextStatus.Error:
      return {
        data: undefined,
        error: new Error('Error loading mission context'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Loading:
      return {
        data: undefined,
        error: null,
        isSuccess: false,
        isLoading: true,
      };
    case MissionContextStatus.NotInitialized:
      return {
        data: undefined,
        error: new Error('Mission context not initialized'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Ready: {
      const generated = (
        <div className={RESOLVER_CONTENT_CLASSNAME}>
          <img alt="Carte de la mission" src={staticMapPreviewUrl} />
        </div>
      );

      return {
        data: { generated },
        error: null,
        isSuccess: true,
        isLoading: false,
      };
    }
    default: {
      throw new Error('Unhandled mission context status');
    }
  }
};
